import type { UseCurriculum_PersonalizedSegmentCourseFragment as PersonalizedSegmentCourseFragment } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import type { DegreePlan_TermPlanFragment as TermPlan } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/learnerPlanFragment';
import type { UseCourseOfferings_CourseOfferingFragment as SegmentCourseOffering } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCourseOfferings';

/**
 * Given a list of term plans, return the plans for past terms
 * @param currentTime the current timestamp in number
 * @param termPlans the list of term plans
 * @returns the list of term plans for past terms
 */
export const getPastTermPlans = (currentTime: number, termPlans: TermPlan[]): TermPlan[] => {
  return termPlans.filter(
    (termPlan) => termPlan.linkedTerm && new Date(termPlan.linkedTerm.endDate).getTime() < currentTime
  );
};

/**
 * Given a list of term plans, return the plans for in-progress terms
 * @param currentTime the current timestamp in number
 * @param termPlans the list of term plans
 * @returns the list of term plans for in-progress terms
 */
export const getInProgressTermPlans = (currentTime: number, termPlans: TermPlan[]): TermPlan[] => {
  return termPlans.filter(
    (termPlan) =>
      termPlan.linkedTerm &&
      new Date(termPlan.linkedTerm.startDate).getTime() < currentTime &&
      new Date(termPlan.linkedTerm.endDate).getTime() > currentTime
  );
};

/**
 * Given a list of term plans, return the plans for future terms
 * @param currentTime the current timestamp in number
 * @param termPlans the list of term plans
 * @returns the list of term plans for future terms
 */
export const getFutureTermPlans = (currentTime: number, termPlans: TermPlan[]): TermPlan[] => {
  return termPlans.filter(
    (termPlan) => !termPlan.linkedTerm || new Date(termPlan.linkedTerm.startDate).getTime() > currentTime
  );
};

/**
 * Given a list of courses, apply deduplication based on segment course id
 * @param courses the list of courses
 * @returns the list of deduped courses
 */
export const dedupSegCourses = <T extends PersonalizedSegmentCourseFragment | SegmentCourseOffering>(
  courses: T[]
): T[] => {
  const segmentCourseDictionary: Record<string, T> = {};

  courses.forEach((course) => {
    const segmentCourseId =
      course.__typename === 'DegreePlan_PersonalizedSegmentCourse'
        ? course.segmentCourse.id
        : (course as SegmentCourseOffering).id;

    segmentCourseDictionary[segmentCourseId] = course;
  });
  return Object.values(segmentCourseDictionary);
};

/**
 * Extract the list of planned segment courses from a term plan
 * @param termPlan the term plan
 * @param requirementTypeData the requirement type data of segment courses, keyed by the segment course ids
 * @return the list of planned segment courses in the term plan
 */
export const getPlannedSegCoursesFromTermPlan = (termPlan: TermPlan): PersonalizedSegmentCourseFragment[] => {
  const plannedSegCourses: PersonalizedSegmentCourseFragment[] = [];

  (termPlan.plannedSegmentCourses ?? []).forEach((plannedCourse) => {
    if (plannedCourse.personalizedSegmentCourse) {
      plannedSegCourses.push(plannedCourse.personalizedSegmentCourse);
    }
  });

  return dedupSegCourses(plannedSegCourses);
};
